/**
 * ReadableModeFeature - Chrome Reader-like fullscreen overlay
 * Extracts main content from <main>, <article>, or #content and displays
 * it in a clean, readable overlay (text only, no images/media)
 */
import BaseFeature from './BaseFeature.js';

export default class ReadableModeFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        this._overlay = null;
    }

    enable() {
        super.enable();
        this._createOverlay();
    }

    disable() {
        super.disable();
        this._removeOverlay();
    }

    /**
     * Find the main content container on the page
     */
    _findContentSource() {
        const selectors = [
            'main#main-content',
            'main',
            'article',
            '[role="main"]',
            '#content',
            '#cdala',
            '.mainContent',
            '#wrap .container'
        ];
        for (const sel of selectors) {
            const el = document.querySelector(sel);
            if (el && el.textContent.trim().length > 100) return el;
        }
        return document.querySelector('#wrap') || document.body;
    }

    /**
     * Extract only text content (headings, paragraphs, lists, links, tables)
     */
    _extractTextContent(source) {
        const clone = source.cloneNode(true);

        // Remove non-text elements from clone
        const removeSelectors = [
            'img', 'video', 'iframe', 'audio', 'canvas', 'svg',
            'figure', 'picture', 'source',
            '.image', '.ce-gallery', '.carousel-img', '.coverflowImageSlide',
            'nav', '.navbar', '.navbar-fixed-top',
            'aside', '.sidebar',
            '.footer-area', '.barrierefrei',
            'script', 'style', 'noscript',
            '.typo3-adminPanel', '#accessibility-widget',
            'form', 'input', 'select', 'textarea', 'button',
            '[style*="background-image"]'
        ];
        removeSelectors.forEach(sel => {
            clone.querySelectorAll(sel).forEach(el => el.remove());
        });

        // Strip all class, style, and id attributes to prevent site CSS leaking in
        clone.querySelectorAll('*').forEach(el => {
            el.removeAttribute('class');
            el.removeAttribute('style');
            el.removeAttribute('id');
        });

        // Remove empty containers
        clone.querySelectorAll('div, span, section').forEach(el => {
            if (!el.textContent.trim()) el.remove();
        });

        return clone.innerHTML;
    }

    /**
     * Get page title
     */
    _getPageTitle() {
        const h1 = document.querySelector('#wrap h1:not(.sr-only), main h1:not(.sr-only), article h1');
        if (h1 && h1.textContent.trim()) return h1.textContent.trim();
        const title = document.title || '';
        return title.split('|')[0].split('-')[0].trim();
    }

    /**
     * Create fullscreen reading overlay
     */
    _createOverlay() {
        if (this._overlay) return;

        const source = this._findContentSource();
        const content = this._extractTextContent(source);
        const title = this._getPageTitle();

        const overlay = document.createElement('div');
        overlay.className = 'dhA11y-reader-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', 'Reading Mode');

        overlay.innerHTML =
            '<div class="dhA11y-reader-topbar">' +
                '<span class="dhA11y-reader-topbar-title">' + (title || 'Reading Mode') + '</span>' +
                '<button class="dhA11y-reader-close" aria-label="Close reading mode">&times;</button>' +
            '</div>' +
            '<div class="dhA11y-reader-scroll">' +
                '<div class="dhA11y-reader-content">' +
                    (title ? '<h1 class="dhA11y-reader-title">' + title + '</h1>' : '') +
                    '<div class="dhA11y-reader-body">' + content + '</div>' +
                '</div>' +
            '</div>';

        document.body.appendChild(overlay);
        this._overlay = overlay;

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Animate in
        requestAnimationFrame(() => {
            overlay.classList.add('dhA11y-reader-visible');
        });

        // Close button
        overlay.querySelector('.dhA11y-reader-close').addEventListener('click', () => {
            this.widget.toggleFeature(this.options.key || 'readableMode', false);
        });

        // ESC key
        this._escHandler = (e) => {
            if (e.key === 'Escape') {
                this.widget.toggleFeature(this.options.key || 'readableMode', false);
            }
        };
        document.addEventListener('keydown', this._escHandler);

        // Focus close button
        overlay.querySelector('.dhA11y-reader-close').focus();
    }

    /**
     * Remove overlay with animation
     */
    _removeOverlay() {
        if (!this._overlay) return;

        document.body.style.overflow = '';

        if (this._escHandler) {
            document.removeEventListener('keydown', this._escHandler);
            this._escHandler = null;
        }

        this._overlay.classList.remove('dhA11y-reader-visible');
        const overlay = this._overlay;
        setTimeout(() => {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        }, 300);
        this._overlay = null;
    }
}
